import type { MachineOutputUnit, MachineRecord } from '../types/records'

export function slotDurationHours(plannedStart: string, plannedEnd: string): number {
  const start = new Date(plannedStart).getTime()
  const end = new Date(plannedEnd).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0
  return (end - start) / (1000 * 60 * 60)
}

/** Норма станка в единицах выпуска за час. */
export function machineOutputPerHour(
  m: Pick<MachineRecord, 'output_quantity' | 'output_period' | 'shift_hours'>,
): number {
  const qty = Number(m.output_quantity) || 0
  if (qty <= 0) return 0

  switch (m.output_period) {
    case 'minute':
      return qty * 60
    case 'hour':
      return qty
    case 'shift': {
      const shiftH = Number(m.shift_hours) || 8
      return shiftH > 0 ? qty / shiftH : 0
    }
    default:
      return 0
  }
}

export type ProductOutputSpec = {
  outputUnit?: MachineOutputUnit
  sheetsPerUnit?: number
}

export type ResolvedProductOutput = 'rolls' | 'sheets' | null

/** Режим выпуска продукции: rolls приоритетнее; sheets — по outputUnit или sheetsPerUnit. */
export function resolveProductOutput(product?: ProductOutputSpec | null): ResolvedProductOutput {
  if (!product) return null
  if (product.outputUnit === 'rolls') return 'rolls'
  if (product.outputUnit === 'sheets') return 'sheets'
  if (product.sheetsPerUnit != null && product.sheetsPerUnit > 0) return 'sheets'
  return null
}

export function productQuantityUnitLabel(product?: ProductOutputSpec | null): string {
  const resolved = resolveProductOutput(product)
  if (resolved === 'rolls') return 'рул.'
  if (resolved === 'sheets') return 'уп.'
  return 'ед.'
}

/** Количество за интервал в единицах станка (листы или рулоны). */
export function machineOutputForInterval(
  machine: Pick<MachineRecord, 'output_quantity' | 'output_period' | 'shift_hours'>,
  plannedStart: string,
  plannedEnd: string,
): number {
  const hours = slotDurationHours(plannedStart, plannedEnd)
  return Math.max(0, hours * machineOutputPerHour(machine))
}

export function productOutputUnitsMatch(
  product: ProductOutputSpec | null | undefined,
  machine: Pick<MachineRecord, 'output_unit'>,
): boolean {
  const resolved = resolveProductOutput(product)
  if (!resolved) return true
  return resolved === machine.output_unit
}

/**
 * План слота в единицах продукции заявки (упаковки для листовой, рулоны для рулонной).
 * Станок считается в своих единицах; для листовой продукции делим на листов в упаковке.
 */
export function calculatePlannedQuantity(
  machine: Pick<MachineRecord, 'output_quantity' | 'output_period' | 'shift_hours' | 'output_unit'>,
  plannedStart: string,
  plannedEnd: string,
  product?: ProductOutputSpec | null,
): number {
  const machineQty = machineOutputForInterval(machine, plannedStart, plannedEnd)
  if (machineQty <= 0) return 0

  const resolved = resolveProductOutput(product)
  if (!resolved) {
    return Math.round(machineQty)
  }

  if (resolved !== machine.output_unit) {
    return 0
  }

  if (resolved === 'rolls') {
    return Math.round(machineQty)
  }

  const sheetsPerUnit = Math.max(1, Number(product?.sheetsPerUnit) || 1)
  return Math.max(0, Math.round(machineQty / sheetsPerUnit))
}

export function plannedQuantityFieldLabel(product?: ProductOutputSpec | null): string {
  const resolved = resolveProductOutput(product)
  if (resolved === 'rolls') return 'План, рул.'
  if (resolved === 'sheets') return 'План, уп.'
  return 'План, шт.'
}

export function plannedQuantityHint(
  machine: Pick<MachineRecord, 'output_quantity' | 'output_period' | 'shift_hours' | 'output_unit'>,
  plannedStart: string,
  plannedEnd: string,
  product?: ProductOutputSpec | null,
): string | null {
  const resolved = resolveProductOutput(product)
  if (!resolved || !machine) return null
  const machineQty = Math.round(machineOutputForInterval(machine, plannedStart, plannedEnd))
  if (machineQty <= 0) return null

  if (resolved === 'rolls') {
    return `Станок за интервал: ${formatQty(machineQty)} рул.`
  }

  const perUnit = Math.max(1, Number(product?.sheetsPerUnit) || 1)
  const packs = Math.round(machineQty / perUnit)
  return `Станок за интервал: ${formatQty(machineQty)} лист. → ${formatQty(packs)} уп. по ${formatQty(perUnit)} л.`
}

/** Листы/рулоны на станке для quantity единиц продукции заявки. */
export function machineUnitsForProductQuantity(
  productQuantity: number,
  product?: ProductOutputSpec | null,
): number {
  const qty = Math.max(0, Number(productQuantity) || 0)
  if (qty <= 0) return 0
  const resolved = resolveProductOutput(product)
  if (!resolved) return qty
  if (resolved === 'rolls') return qty
  const sheetsPerUnit = Math.max(1, Number(product?.sheetsPerUnit) || 1)
  return qty * sheetsPerUnit
}

/** Часы работы станка для выпуска productQuantity единиц продукции. */
export function hoursForProductQuantity(
  machine: Pick<MachineRecord, 'output_quantity' | 'output_period' | 'shift_hours' | 'output_unit'>,
  productQuantity: number,
  product?: ProductOutputSpec | null,
): number {
  if (!productOutputUnitsMatch(product, machine)) return 0
  const perHour = machineOutputPerHour(machine)
  if (perHour <= 0) return 0
  const machineUnits = machineUnitsForProductQuantity(productQuantity, product)
  if (machineUnits <= 0) return 0
  return machineUnits / perHour
}

/** datetime-local → ISO для API PocketBase. */
export function localDateTimeToIso(local: string): string {
  const d = new Date(local)
  if (!Number.isFinite(d.getTime())) return local
  return d.toISOString()
}

/** Date → значение для input[type=datetime-local] (локальное время). */
export function formatLocalDateTimeInput(d: Date): string {
  if (!Number.isFinite(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Прибавить часы к datetime-local, без сдвига UTC. */
export function addHoursToLocalDateTime(startLocal: string, hours: number): string {
  const start = new Date(startLocal)
  if (!Number.isFinite(start.getTime()) || hours <= 0) return ''
  return formatLocalDateTimeInput(new Date(start.getTime() + hours * 3600000))
}

export function formatDurationHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return '—'
  if (hours < 1) return `${Math.round(hours * 60)} мин`
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (m === 0) return `${h} ч`
  return `${h} ч ${m} мин`
}

/** Отклонение факта от плана, %. null если нет факта или план = 0. */
export function deviationPercent(planned: number, actual?: number | null): number | null {
  const p = Number(planned) || 0
  if (actual == null || p <= 0) return null
  return Math.round(((Number(actual) - p) / p) * 1000) / 10
}

export function outputDeviation(planned: number, actual?: number | null): number | null {
  if (actual == null) return null
  return (Number(actual) || 0) - (Number(planned) || 0)
}

export const SCHEDULE_SLOT_STATUS_LABELS: Record<string, string> = {
  planned: 'Запланирован',
  in_progress: 'В работе',
  completed: 'Завершён',
  cancelled: 'Отменён',
}

export const MACHINE_EVENT_REASON_LABELS: Record<string, string> = {
  breakdown: 'Поломка',
  setup: 'Перналадка',
  no_materials: 'Нет материалов',
  maintenance: 'Техобслуживание',
  other: 'Прочее',
}

export function formatQty(value: number): string {
  return (Number(value) || 0).toLocaleString('ru-RU')
}

export function formatDateTimeRu(iso: string): string {
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return iso
  return d.toLocaleString('ru-RU')
}

export function formatDateRu(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return '—'
  return d.toLocaleDateString('ru-RU')
}

export function formatDeviation(planned: number, actual?: number | null): string {
  const d = outputDeviation(planned, actual)
  if (d == null) return '—'
  const sign = d > 0 ? '+' : ''
  return `${sign}${formatQty(d)}`
}

/** Длительность смены в часах (если окончание раньше начала — смена через полночь). */
export function shiftDurationHours(startTime: string, endTime: string): number {
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  let startMin = sh * 60 + (sm || 0)
  let endMin = eh * 60 + (em || 0)
  if (endMin <= startMin) endMin += 24 * 60
  return (endMin - startMin) / 60
}

export { isLineOverdue } from './requestLineStatus'

export type ShiftWindowSpec = {
  startTime: string
  endTime: string
}

function dateKeyFromMs(ms: number): string {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function startOfDayFromMs(ms: number): number {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

function addDaysToMs(ms: number, days: number): number {
  const d = new Date(ms)
  d.setDate(d.getDate() + days)
  return d.getTime()
}

function shiftBoundaryMs(dayMs: number, timeStr: string): number {
  const [h, m] = (timeStr || '08:00').split(':').map(Number)
  const d = new Date(dayMs)
  d.setHours(h || 0, m || 0, 0, 0)
  return d.getTime()
}

export type ShiftScheduleSegment = {
  startMs: number
  endMs: number
  durationHours: number
}

/**
 * Разбить объём работы (часы) на отрезки внутри окон смены (08:00–20:00 и т.д.).
 * Остаток переносится на следующий рабочий день.
 */
export function splitHoursAcrossShiftWindows(
  cursorMs: number,
  totalHours: number,
  shift: ShiftWindowSpec,
  options?: { isWorkingDay?: (dateKey: string) => boolean },
): { segments: ShiftScheduleSegment[]; nextCursorMs: number } {
  const isWorkingDay = options?.isWorkingDay ?? (() => true)
  const segments: ShiftScheduleSegment[] = []
  let remaining = Math.max(0, totalHours)
  let cursor = cursorMs

  while (remaining > 1e-6) {
    let dayStart = startOfDayFromMs(cursor)
    let dateKey = dateKeyFromMs(dayStart)

    while (!isWorkingDay(dateKey)) {
      dayStart = addDaysToMs(dayStart, 1)
      dateKey = dateKeyFromMs(dayStart)
      cursor = shiftBoundaryMs(dayStart, shift.startTime)
    }

    const winStart = shiftBoundaryMs(dayStart, shift.startTime)
    const winEnd = shiftBoundaryMs(dayStart, shift.endTime)
    let startMs = Math.max(cursor, winStart)

    if (startMs >= winEnd) {
      cursor = shiftBoundaryMs(addDaysToMs(dayStart, 1), shift.startTime)
      continue
    }

    const availableHours = (winEnd - startMs) / 3600000
    const chunkHours = Math.min(remaining, availableHours)
    const endMs = startMs + chunkHours * 3600000

    segments.push({ startMs, endMs, durationHours: chunkHours })
    remaining -= chunkHours

    if (remaining > 1e-6) {
      let nextDay = addDaysToMs(dayStart, 1)
      let nextKey = dateKeyFromMs(nextDay)
      while (!isWorkingDay(nextKey)) {
        nextDay = addDaysToMs(nextDay, 1)
        nextKey = dateKeyFromMs(nextDay)
      }
      cursor = shiftBoundaryMs(nextDay, shift.startTime)
    } else {
      cursor = endMs
    }
  }

  return { segments, nextCursorMs: cursor }
}

/** Распределить количество продукции пропорционально длительности сегментов. */
export function distributeQuantityAcrossSegments(
  totalQty: number,
  segments: Pick<ShiftScheduleSegment, 'durationHours'>[],
): number[] {
  const totalHours = segments.reduce((s, x) => s + x.durationHours, 0)
  if (totalHours <= 0 || segments.length === 0) return segments.map(() => 0)

  const out: number[] = []
  let assigned = 0
  for (let i = 0; i < segments.length; i++) {
    if (i === segments.length - 1) {
      out.push(Math.max(0, Math.round(totalQty) - assigned))
    } else {
      const q = Math.round((Math.round(totalQty) * segments[i].durationHours) / totalHours)
      out.push(q)
      assigned += q
    }
  }
  return out
}

export function hourLabels(startHour: number, count: number): string[] {
  return Array.from({ length: count }, (_, i) => {
    const h = (startHour + i) % 24
    return `${String(h).padStart(2, '0')}:00`
  })
}

/** Стиль полоски Gantt: left% и width% от суток. */
export function ganttBarStyle(
  startIso: string,
  endIso: string,
  rangeStart: Date,
  hoursInDay: number,
): { left: string; width: string } | null {
  const dayStart = rangeStart.getTime()
  const dayMs = hoursInDay * 3600000
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null

  const clipStart = Math.max(start, dayStart)
  const clipEnd = Math.min(end, dayStart + dayMs)
  if (clipEnd <= clipStart) return null

  const left = ((clipStart - dayStart) / dayMs) * 100
  const width = ((clipEnd - clipStart) / dayMs) * 100
  return { left: `${left}%`, width: `${Math.max(width, 0.4)}%` }
}
